// client/src/components/secure/DocumentManager.js
import React, { useState, useEffect, useCallback } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import { ProtectedLink } from '../../utils/openProtected';

const DEFAULT_DOCUMENT_TYPES = ['Employment Agreement', 'Independent Contractor Agreement', 'Commission Statement', 'Training Record', 'License Copy', 'Other'];

// Irregular plurals for API route segments (e.g. "property" -> "properties", not "propertys")
const IRREGULAR_PLURALS = { property: 'properties' };
const pluralize = (type) => IRREGULAR_PLURALS[type] || `${type}s`;

// Only these two document tables have a client-portal visibility flag.
const SUPPORTS_CLIENT_VISIBILITY = ['client', 'transaction'];

const DocumentManager = ({ relatedId, relatedType, documentTypes = DEFAULT_DOCUMENT_TYPES }) => {
    const [documents, setDocuments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [fetchError, setFetchError] = useState('');

    const [file, setFile] = useState(null);
    const [documentName, setDocumentName] = useState('');
    const [documentType, setDocumentType] = useState(documentTypes[0]);
    const [isUploading, setIsUploading] = useState(false);

    const apiClient = useApiClient();

    const fetchDocuments = useCallback(async () => {
        setIsLoading(true);
        setFetchError('');
        try {
            const data = await apiClient.get(`/api/${pluralize(relatedType)}/${relatedId}/documents`);
            setDocuments(data);
        } catch (err) {
            setFetchError(err.message || 'Failed to fetch documents.');
        } finally {
            setIsLoading(false);
        }
    }, [relatedId, relatedType, apiClient]);

    useEffect(() => {
        fetchDocuments();
    }, [fetchDocuments]);

    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
    };

    const handleUpload = async (e) => {
        e.preventDefault();
        if (!file || !documentName || !documentType) {
            setError('Please provide a document name, type, and file.');
            return;
        }
        setIsUploading(true);
        setError('');

        const formData = new FormData();
        formData.append('documentFile', file);
        formData.append('document_name', documentName);
        formData.append('document_type', documentType);

        try {
            const newDocument = await apiClient.post(`/api/${pluralize(relatedType)}/${relatedId}/documents`, formData, true);
            setDocuments(prev => [newDocument, ...prev]);
            // Reset form
            setFile(null);
            setDocumentName('');
            document.getElementById('file-input').value = '';
        } catch (err) {
            setError(err.message || 'Failed to upload document.');
        } finally {
            setIsUploading(false);
        }
    };

    const handleToggleVisibility = async (doc) => {
        try {
            await apiClient.put(`/api/${pluralize(relatedType)}/${relatedId}/documents/${doc.document_id}/visibility`, {
                visible_to_client: !doc.visible_to_client,
            });
            setDocuments(prev => prev.map(d => d.document_id === doc.document_id ? { ...d, visible_to_client: !d.visible_to_client } : d));
        } catch (err) {
            setError(err.message || 'Failed to update visibility.');
        }
    };

    const handleDelete = async (documentId) => {
        if (!window.confirm('Are you sure you want to delete this document? This cannot be undone.')) {
            return;
        }
        try {
            await apiClient.delete(`/api/${pluralize(relatedType)}/${relatedId}/documents/${documentId}`);
            setDocuments(prev => prev.filter(doc => doc.document_id !== documentId));
        } catch (err) {
            setError(err.message || 'Failed to delete document.');
        }
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-xl font-bold text-slate-800 mb-4">Document Management</h3>
            
            {/* Upload Form */}
            <form onSubmit={handleUpload} className="mb-6 p-4 border rounded-md bg-slate-50">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <input
                        type="text"
                        placeholder="Document Name (e.g., '2025 W-9')"
                        value={documentName}
                        onChange={(e) => setDocumentName(e.target.value)}
                        className="p-2 border rounded"
                        required
                    />
                    <select value={documentType} onChange={(e) => setDocumentType(e.target.value)} className="p-2 border rounded">
                        {documentTypes.map(type => <option key={type} value={type}>{type}</option>)}
                    </select>
                    <input id="file-input" type="file" onChange={handleFileChange} className="p-2" required />
                </div>
                <button type="submit" disabled={isUploading} className="mt-4 px-4 py-2 bg-slate-800 text-white rounded disabled:bg-indigo-400">
                    {isUploading ? 'Uploading...' : 'Upload Document'}
                </button>
                {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
            </form>

            {/* Document List */}
            <div className="space-y-3">
                {isLoading && <p>Loading documents...</p>}
                {!isLoading && fetchError && <p className="text-red-500">{fetchError}</p>}
                {!isLoading && !fetchError && documents.length === 0 && <p className="text-slate-500">No documents uploaded yet.</p>}
                {documents.map(doc => (
                    <div key={doc.document_id} className="flex justify-between items-center p-3 border rounded-md hover:bg-slate-50">
                        <div>
                            <p className="font-semibold text-slate-700">{doc.document_name}</p>
                            <p className="text-sm text-slate-500">Type: {doc.document_type} | Uploaded: {new Date(doc.upload_timestamp).toLocaleDateString()}</p>
                        </div>
                        <div className="flex items-center gap-4">
                            {SUPPORTS_CLIENT_VISIBILITY.includes(relatedType) && (
                                <label className="flex items-center gap-1 text-sm text-slate-600">
                                    <input type="checkbox" checked={!!doc.visible_to_client} onChange={() => handleToggleVisibility(doc)} />
                                    Visible to client
                                </label>
                            )}
                            <ProtectedLink href={doc.storage_path} className="text-indigo-600 hover:underline">View</ProtectedLink>
                            <button onClick={() => handleDelete(doc.document_id)} className="text-red-500 hover:underline">Delete</button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default DocumentManager;